import { useRef, useCallback, useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { ALLOWED_DOC_TYPES, ALLOWED_MEDIA_TYPES } from "@/config/constants";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import {
  extractLinksFromText,
  isCompleteUrl,
  shouldTriggerLinkExtraction,
} from "@/lib/linkExtractor";
import { useNavigationState } from "@/zustand/NavigationState";
import { useRtfInputForChat } from "@/hooks/useRtfInputForChat";
import { IS_MOBILE } from "@/lib/environmentDetector";

/**
 * Purpose: Custom hook to manage the markdown textarea input
 *
 * Replaces the TipTap editor with a simple textarea-based approach.
 * Users type raw markdown which is sent as-is.
 *
 * Key features:
 * - Device-aware Enter key behavior (new line on mobile, send on desktop)
 * - File and media paste handling
 * - Draft persistence
 * - Link extraction from plain text
 */
export function useRtfEditor(
  handleSendMessage: () => void,
  notifyTyping?: (text: string) => void,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const {
    setDraftMessage,
    clearDraftMessage,
    setLinkMessage,
    clearLinkMessage,
    clearLinkMessageMetadata,
    linkMessage,
    replyMessage,
    editMessage,
  } = useRtfInputForChat(activeChatId);

  const { handleMediaAttachment } = useMediaMessageSenderState();
  const { handleFileAttachment } = useFileMessageSenderState();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState("");

  // Keep reactive values in refs so updateValue doesn't need them as deps.
  // This prevents cascading re-creations of handleChange / setContent / etc.
  const chatStateRef = useRef({ linkMessage, replyMessage, editMessage });
  chatStateRef.current = { linkMessage, replyMessage, editMessage };

  const prevValueRef = useRef("");

  const updateValue = useCallback(
    (newValue: string, fromUser = false) => {
      setValue(newValue);

      // Drive the typing indicator ONLY for genuine user input (keystrokes,
      // emoji/toolbar insertions). Programmatic hydration — draft restore and
      // entering edit mode both route through setContent -> updateValue — must
      // NOT emit typing=true, or merely opening such a chat would falsely show
      // the user as typing. The emitter handles throttling, the empty -> stop
      // transition, and lifecycle; this is just the keystroke signal.
      if (fromUser && activeChatId) {
        notifyTyping?.(newValue);
      }

      const prevValue = prevValueRef.current;
      prevValueRef.current = newValue;

      if (activeChatId) {
        if (newValue.trim().length === 0) {
          clearDraftMessage();
          clearLinkMessage();
          clearLinkMessageMetadata();
        } else {
          setDraftMessage(newValue);

          const currentLink = chatStateRef.current.linkMessage;
          const currentReply = chatStateRef.current.replyMessage;
          const currentEdit = chatStateRef.current.editMessage;

          if (shouldTriggerLinkExtraction(prevValue, newValue)) {
            const links = extractLinksFromText(newValue).filter(isCompleteUrl);
            if (links.length > 0 && !currentReply && !currentEdit) {
              // Update link preview when the first extracted link changes
              if (!currentLink || currentLink !== links[0]) {
                clearLinkMessageMetadata();
                setLinkMessage(links[0]);
              }
            } else {
              clearLinkMessage();
              clearLinkMessageMetadata();
            }
          } else if (currentLink && !currentReply && !currentEdit) {
            // Single non-whitespace char typed — don't search for new links,
            // but clear a stale preview if the URL is no longer in the text
            const presentLinks =
              extractLinksFromText(newValue).filter(isCompleteUrl);
            if (!presentLinks.includes(currentLink)) {
              clearLinkMessage();
              clearLinkMessageMetadata();
            }
          }
        }
      }
    },
    [
      activeChatId,
      notifyTyping,
      setDraftMessage,
      clearDraftMessage,
      setLinkMessage,
      clearLinkMessage,
      clearLinkMessageMetadata,
    ],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      updateValue(e.target.value, true);
    },
    [updateValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // On mobile, Enter always inserts a new line
      if (IS_MOBILE) {
        return;
      }

      // On desktop: Enter sends, Shift+Enter inserts new line
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const pastedFiles = e.clipboardData?.files;
      if (!pastedFiles || pastedFiles.length === 0) {
        return;
      }

      const file = pastedFiles[0];
      if (!file) {
        return;
      }

      const isMedia = ALLOWED_MEDIA_TYPES.includes(file.type);
      const isPdf = ALLOWED_DOC_TYPES.includes(file.type);

      if (isMedia) {
        e.preventDefault();
        handleMediaAttachment(file);
      } else if (isPdf) {
        e.preventDefault();
        handleFileAttachment(file);
      } else {
        toast({
          duration: 1000,
          description: t("fileTypeNotSupported"),
          variant: "destructive",
        });
      }
    },
    [handleMediaAttachment, handleFileAttachment, toast, t],
  );

  const clearContent = useCallback(() => {
    updateValue("");
  }, [updateValue]);

  const setContent = useCallback(
    (content: string) => {
      // Route through updateValue so draft / link side effects fire
      updateValue(content);
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    },
    [updateValue],
  );

  const focus = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  const insertText = useCallback(
    (text: string) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        updateValue(value + text, true);
        return;
      }

      const { selectionStart, selectionEnd } = textarea;
      const newValue =
        value.slice(0, selectionStart) + text + value.slice(selectionEnd);
      updateValue(newValue, true);

      // Set cursor after inserted text
      const newPos = selectionStart + text.length;
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      });
    },
    [value, updateValue],
  );

  return {
    textareaRef,
    value,
    updateValue,
    handleChange,
    handleKeyDown,
    handlePaste,
    clearContent,
    setContent,
    focus,
    insertText,
    isEmpty: value.trim().length === 0,
  };
}
